import {
  ErrorBoundary,
  SectionContainer,
  Select,
  SelectTypes,
  Table,
  Text,
  Icon,
  HStack,
} from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Box, Center } from 'native-base';
import React, { useMemo, useState } from 'react';
import { FlatList } from 'react-native';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { TopProductsQuery } from '../../../../store/home/capture/selector';

import { Top15ProductsItem } from './components/Top15ProductsItem';
import { rowHeight, maxHeight, maxHeightMobile } from './constants';

export namespace Top15ProductsTypes {
  export interface Props {
    isMobile?: boolean;
  }
}

export const Top15Products: React.FC<Top15ProductsTypes.Props> = ({ isMobile }) => {
  const [selectedViewOption] = useState('prev');
  const [selectedPeriod] = useState('prev');

  const {
    data: topProducts,
    loading: topProductsIsLoading,
    error: topProductsHasError,
    refresher: topProductsRefresh,
  } = useRecoilQuery(TopProductsQuery);

  const filterOptions: SelectTypes.Option[] = [
    {
      label: `ATUAL ${topProducts?.relatedDateNormalized}`,
      value: 'prev',
    },
  ];

  const handlePeriodChange = (_value: string) => {
    // NOOP;
  };

  const content = useMemo(() => {
    if (topProductsHasError) {
      return (
        <Center flex={1}>
          <ErrorBoundary onRefresh={topProductsRefresh} />
        </Center>
      );
    }

    if (!topProductsIsLoading && topProducts?.products && topProducts?.products.length <= 0) {
      return (
        <Center flex={1}>
          <ErrorBoundary
            errorIconOrIllustration="NotFoundIllustration"
            errorTitle="Nenhum resultado corresponde à sua pesquisa"
            errorMessage="Não há resultados correspondentes a este período de 30 dias"
          />
        </Center>
      );
    }

    if (selectedViewOption === 'prev') {
      return (
        <Table
          testID="table"
          isLoading={topProductsIsLoading}
          rows={4}
          cols={4}
          rowHeight={rowHeight}
          flex={1}>
          <FlatList
            data={topProducts?.products}
            windowSize={10}
            maxToRenderPerBatch={4}
            updateCellsBatchingPeriod={5}
            keyExtractor={(_, index) => index.toString()}
            contentContainerStyle={{ maxHeight: !isMobile ? maxHeight : maxHeightMobile }}
            showsVerticalScrollIndicator={false}
            renderItem={({ item, index }) => <Top15ProductsItem item={item} index={index} />}
          />
        </Table>
      );
    }
  }, [
    topProductsHasError,
    selectedViewOption,
    topProductsRefresh,
    topProductsIsLoading,
    isMobile,
    topProducts,
  ]);

  if (isMobile) {
    return (
      <Box width={{ base: 'full', md: '4/6' }} paddingX={Spacing['stack.xxxs']} flex={1}>
        <HStack paddingBottom={Spacing['stack.xxs']} alignItems="center">
          <HStack space={Spacing['stack.quarck']} alignItems="center">
            <Icon size="xs" name="Calendar" color={Color['neutral.medium']} />
            <Text color="neutral.medium" fontSize={Typography['fontsize.md']}>
              Período
            </Text>
          </HStack>
          <Select
            testID="period-select"
            onValueChange={handlePeriodChange}
            selectedValue={selectedPeriod}
            options={filterOptions}
            placeholder="Selecione um período"
          />
        </HStack>
        {content}
      </Box>
    );
  }

  return (
    <Box flex={1}>
      <SectionContainer
        flex={1}
        title="TOP 15 PRODUTOS"
        withFilter
        withSeparator
        multipleFilterLabel="Período:"
        tooltipLabel="O gráfico relaciona o patrimônio nos 15 produtos com maiores alocações de um conjunto de clientes."
        filterOptions={filterOptions}
        selectedOption={selectedViewOption}
        onFilterChange={handlePeriodChange}>
        {content}
      </SectionContainer>
    </Box>
  );
};
